import {
  AdminSourceResponse,
  RootStatus,
  AdminUserDetailsResponse,
  WebsiteResponse,
} from "@authory-packages/core";
import { Box, Typography } from "@material-ui/core";
import { SourceDetails } from "./SourceDetails";
import { sourceColors } from "./SourceDetails-utils";
import { useEffect, useState } from "react";
import { Api } from "../../lib/api";

type SourceManagerProps = {
  userSlug: string;
  user: AdminUserDetailsResponse;
  userWebsites: WebsiteResponse[];
  onSourceDeleted: () => void;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface SourceWithColorAndParent extends AdminSourceResponse {
  uiColorCode: string;
  parentSource?: AdminSourceResponse;
}

export const SourceManager = ({
  userSlug,
  userWebsites,
  user,
  onSourceDeleted,
}: SourceManagerProps) => {
  const [sources, setAdminSources] = useState<AdminSourceResponse[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [sourcesWithColors, setSourcesWithColours] = useState<
    SourceWithColorAndParent[]
  >([]);

  useEffect(() => {
    const getWebsites = async () => {
      for (let index = 0; index < userWebsites.length; index++) {
        const src = userWebsites[index];
        const loadedAlready = sources.find(
          (it) => it.canonicalSlug === src.canonicalSlug
        );
        if (!loadedAlready) {
          const website = await Api.getUserWebsite(userSlug, src.canonicalSlug);
          setAdminSources((curr) =>
            [...curr, website.data].sort((a, b) => a.id - b.id)
          );
          await sleep(250);
        }
      }
      setSourcesLoaded(true);
    };

    getWebsites();
  }, [userWebsites]);

  const updateSourcesCallback = async (
    canonicalSlug: string,
    isDelete?: boolean
  ) => {
    if (isDelete) {
      onSourceDeleted();
      setAdminSources((curr) =>
        curr.filter((src) => src.canonicalSlug !== canonicalSlug)
      );
    } else {
      const website = await Api.getUserWebsite(userSlug, canonicalSlug);
      setAdminSources((curr) =>
        curr.map((src) =>
          src.canonicalSlug === canonicalSlug ? website.data : src
        )
      );
    }
  };

  useEffect(() => {
    const srcColor = [];
    let j = -1;

    for (const src of sources) {
      if (!src.parentCanonicalSlug) {
        j++;
      }
      srcColor.push({
        ...src,
        uiColorCode: sourceColors[j],
        parentSource: src.parentCanonicalSlug
          ? sources.find(
              (src2) => src2.canonicalSlug === src.parentCanonicalSlug
            )
          : undefined,
      });
    }

    setSourcesWithColours(srcColor);
  }, [sources]);

  const newWebsites: SourceWithColorAndParent[] = [];
  const inProgressWebsites: SourceWithColorAndParent[] = [];
  const importFinishedWebsites: SourceWithColorAndParent[] = [];
  const continuousCrawlWebsites: SourceWithColorAndParent[] = [];
  const awaitingFeedback: SourceWithColorAndParent[] = [];
  const sourceTuning: SourceWithColorAndParent[] = [];
  const autoDisabled: SourceWithColorAndParent[] = [];
  const deletedSources = sourcesWithColors.filter((src) => src.deleted);
  const nonDeletedSources = sourcesWithColors.filter((src) => !src.deleted);

  nonDeletedSources.forEach((website) => {
    switch (website.stage) {
      case RootStatus.New:
        newWebsites.push(website);
        break;
      case RootStatus.FullCrawlAnalyzeRunning:
      case RootStatus.FullCrawlRunning:
      case RootStatus.SearchEngineFullCrawlRunning:
        inProgressWebsites.push(website);
        break;
      case RootStatus.AwaitingFeedback:
        awaitingFeedback.push(website);
        break;
      case RootStatus.ImportFinished:
        importFinishedWebsites.push(website);
        break;
      case RootStatus.AutoDisabled:
        autoDisabled.push(website);
        break;
      case RootStatus.SourceTuning:
        sourceTuning.push(website);
        break;
      default:
        continuousCrawlWebsites.push(website);
        break;
    }
  });

  // Inactive websites are determined by the active property and not just the root status.
  const activeCCWebsites = continuousCrawlWebsites.filter((src) => src.active);
  const inactiveCCWebsites = continuousCrawlWebsites.filter(
    (src) => !src.active
  );

  return (
    <>
      {userWebsites.length !== sources.length && (
        <Box mt={2}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div>
              Loading user sources {sources.length} out of {userWebsites.length}
            </div>
          </div>
        </Box>
      )}
      {!!sourceTuning.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Source Tuning
            </Typography>
          </Box>
          {sourceTuning.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!newWebsites.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              New Websites
            </Typography>
          </Box>
          {newWebsites.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!importFinishedWebsites.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Import finished
            </Typography>
          </Box>
          {importFinishedWebsites.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!inProgressWebsites.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              In progress
            </Typography>
          </Box>
          {inProgressWebsites.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!awaitingFeedback.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Awaiting Feedback
            </Typography>
          </Box>
          {awaitingFeedback.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}

      {!!activeCCWebsites.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Continuous Crawl
            </Typography>
          </Box>
          {activeCCWebsites.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!inactiveCCWebsites.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Inactive Sources
            </Typography>
          </Box>
          {inactiveCCWebsites.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!autoDisabled.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Auto Disabled
            </Typography>
          </Box>
          {autoDisabled.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
      {!!deletedSources.length && (
        <>
          <Box mt={2}>
            <Typography variant="h6" color="primary">
              Deleted Sources
            </Typography>
          </Box>
          {deletedSources.map((website) => (
            <SourceDetails
              sourcesLoaded={sourcesLoaded}
              key={website.id}
              source={website}
              userSlug={userSlug}
              user={user}
              updateSourcesCallback={updateSourcesCallback}
            />
          ))}
        </>
      )}
    </>
  );
};
